import { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";
import { getJaDisplayName } from "components/userUtil";

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function PasswordResetDialog({
  open,
  onClose,
}: ConfirmationDialogProps) {
  const [email, setEmail] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevOpenRef = useRef(open);
  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const isDisabled = useMemo(() => {
    return !email.trim() || !emailPattern.test(email) || loading;
  }, [email, loading]);

  useEffect(() => {
    if (!prevOpenRef.current && open) {
      setShowConfirmation(false);
      setEmail("");
      setError(null);
    }

    prevOpenRef.current = open;
  }, [open]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(null);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);

      await sendPasswordResetEmail(auth, email);

      setShowConfirmation(true);
    } catch (err: any) {
      console.error(err);
      if (err.code === "auth/user-not-found") {
        setError("指定されたメールアドレスのユーザーが見つかりませんでした。");
      } else {
        setError("パスワードリセットメールの送信に失敗しました。");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>パスワードリセット</DialogTitle>
      <DialogContent>
        {!showConfirmation ? (
          <>
            <DialogContentText>
              登録したメールアドレスにパスワードリセットリンクを送信します。
              よろしいですか？
            </DialogContentText>
            <Box sx={{ mt: 2, mb: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                id="email"
                label={getJaDisplayName("email")}
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
              />
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </>
        ) : (
          <DialogContentText>
            {email} 宛にパスワードリセットリンクを送信しました。
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {!showConfirmation ? (
          <>
            <Button onClick={onClose} disabled={loading}>
              いいえ
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              disabled={isDisabled}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {loading ? "送信中..." : "はい"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained" autoFocus>
            閉じる
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
